// TDU stage-map — marker classification.
//
// Grounded in the real plotaroute exports (see __fixtures__/2027-christies-to-willunga.kml):
//   * <description> is empty CDATA — it carries NO classification signal (contra plan v2 §6.4).
//   * Plotaroute truncates <name> to ~10 chars: "Start Race", "King / Que", "Feed &#38;",
//     "Ziptrak® S", "Race Finis", "FINISH", "Coxs Hill". So we PREFIX-match truncated names.
//   * ~half the markers are turn-by-turn navigation cues ("Right turn", "Veer left", …) that
//     must be filtered out before anything race-related is shown.
//   * Some real features are location-named ("Coxs Hill" = a KOM) and so are NOT auto-detectable
//     from the name — those depend on the editor-override layer. classify() flags them as
//     `WAYPOINT` with needsReview=true so the editor UI can surface them for labelling.

/** @typedef {'START_NEUTRAL'|'END_NEUTRAL'|'KOM'|'SPRINT'|'FEED'|'FINISH'|'START'|'VANTAGE'|'NAV'|'WAYPOINT'} MarkerType */

/** Race-feature types that should render as iconography on the map. */
export const RACE_FEATURE_TYPES = ['START_NEUTRAL', 'END_NEUTRAL', 'KOM', 'SPRINT', 'FEED', 'FINISH', 'START', 'VANTAGE'];

// Some plotaroute exports carry the OFFICIAL climb stats in a KOM placemark <description>, e.g.
// "Distance: 2.16km, Total Ascent 79m, Maximum Gradient: 8.4%, Average Gradient: 3.9%". When present
// that is the authoritative grade (the organiser's figure), so prefer it over a gradient derived
// from noisy SRTM elevation. Returns the average gradient as a positive % (a KOM is always a climb),
// or null when the description has no such figure (e.g. the empty-CDATA exports). Tolerant of the
// entity-encoded / whitespace-padded text these exports emit.
export function parseAverageGradient(description) {
    if (!description) return null;
    const m = /average\s*gradient:?\s*([\d.]+)\s*%/i.exec(decodeEntities(String(description)));
    if (!m) return null;
    const pct = Number(m[1]);
    return Number.isFinite(pct) ? pct : null;
}

// Decode the handful of XML/HTML entities plotaroute emits in names (e.g. "Feed &#38; Clean").
export function decodeEntities(s) {
    if (!s) return '';
    return s
        .replace(/&#(\d+);/g, (_, d) => String.fromCodePoint(Number(d)))
        .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCodePoint(parseInt(h, 16)))
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&apos;/g, "'");
}

// Turn-by-turn navigation cues. Order/spelling taken from the real export.
const NAV_CUE = /^(right turn|left turn|turn (right|left|around)|veer|keep (left|right)|sharp (left|right)|continue|slight|roundabout|take the|exit|merge|at the|head|proceed|bear)\b/i;

// Race-feature prefix patterns, matched against the (decoded, trimmed) truncated name.
// First match wins, so order from most-specific to least.
const FEATURE_PATTERNS = [
    { type: 'START_NEUTRAL', re: /^start race|^start neutral|neutral start/i },
    { type: 'END_NEUTRAL', re: /^end race|^end neutral|neutral end|^end of (race )?neutral/i },
    { type: 'SPRINT', re: /ziptrak|sprint/i },
    // "King of the Mountain" / "Queen of the Mountain" appear in full and as plotaroute's ~10-char
    // truncations ("King of th", "Queen of t", "King / Que"). Match the "king/queen of" lead-in and
    // the truncated slash form, plus KOM/QOM and any "mountain"/"climb".
    { type: 'KOM', re: /^(king|queen) ?\/ ?(que|kin)|^(king|queen) of|\b[kq]om\b|mountain|^climb/i },
    { type: 'FEED', re: /^feed/i },
    { type: 'FINISH', re: /^finish|^race finis|^stage finish/i },
    { type: 'VANTAGE', re: /^vantage|viewing (area|point)|spectator/i }, // present in some KMLs (e.g. 2026 Tanunda ×4)
    { type: 'START', re: /^start\b/i }, // generic start, after START_NEUTRAL
];

/**
 * Classify a raw marker name from the route file.
 * @param {string} rawName
 * @returns {{ type: MarkerType, isRaceFeature: boolean, needsReview: boolean, label: string }}
 */
export function classifyMarker(rawName) {
    const label = decodeEntities(rawName || '').trim();

    if (!label) return { type: 'WAYPOINT', isRaceFeature: false, needsReview: false, label };

    if (NAV_CUE.test(label)) {
        return { type: 'NAV', isRaceFeature: false, needsReview: false, label };
    }

    for (const { type, re } of FEATURE_PATTERNS) {
        if (re.test(label)) {
            return { type, isRaceFeature: true, needsReview: false, label };
        }
    }

    // A named, non-nav marker we couldn't map (e.g. a location-named KOM like "Coxs Hill",
    // or a street-name waypoint). Surface for the editor to confirm/override or discard.
    return { type: 'WAYPOINT', isRaceFeature: false, needsReview: true, label };
}

/**
 * Apply editor overrides on top of auto-classification. An override keys off the marker's
 * stable identity (index in the source file) and can set an explicit type or hide a marker.
 * This is load-bearing, not polish: the source data cannot fully self-classify (§6.3/§6.5).
 * @param {{type: MarkerType, label: string}} auto
 * @param {{type?: MarkerType, label?: string, hidden?: boolean}} [override]
 */
export function applyOverride(auto, override) {
    if (!override) return auto;
    if (override.hidden) return { ...auto, type: 'NAV', isRaceFeature: false, hidden: true };
    const type = override.type || auto.type;
    const activeLaps = parseActiveLaps(override.activeLaps);
    return {
        ...auto,
        type,
        label: override.label ?? auto.label,
        isRaceFeature: RACE_FEATURE_TYPES.includes(type),
        needsReview: false,
        ...(activeLaps ? { activeLaps } : {}),
    };
}

/**
 * Parse an editor "active laps" value (e.g. "1,2" or "1, 3") into a sorted, deduped array of 1-based
 * pass numbers, or null when blank/garbage. Lets an editor pin which laps a repeated circuit feature
 * is contested on, overriding the geometric default in buildStage's selectActivePasses.
 */
export function parseActiveLaps(value) {
    if (value == null) return null;
    const laps = String(value)
        .split(/[,\s]+/)
        .map((s) => parseInt(s, 10))
        .filter((n) => Number.isInteger(n) && n >= 1);
    return laps.length ? [...new Set(laps)].sort((a, b) => a - b) : null;
}

// An author can make the KML self-describing by adding a "Laps: 1,2" directive to a repeated
// feature's placemark <description> (the by-the-source alternative to the back-office "Point laps"
// field). Matches "Laps:"/"Lap:" followed by a comma list; stops at the first non-number so it never
// eats following text (e.g. a KOM's "Average Gradient" stats).
const LAPS_DIRECTIVE = /\blaps?\s*:\s*(\d+(?:\s*,\s*\d+)*)/i;

/** Active laps from a KML description directive ("Laps: 1,2"), or null when absent. */
export function parseLapsDirective(description) {
    if (!description) return null;
    const m = LAPS_DIRECTIVE.exec(decodeEntities(String(description)));
    return m ? parseActiveLaps(m[1]) : null;
}

/** Strip the "Laps: …" directive from a description so it isn't shown in the popup. */
export function stripLapsDirective(description) {
    if (!description) return description;
    return String(description).replace(LAPS_DIRECTIVE, '').trim();
}

/**
 * Parse the editor "point laps" text into match→laps rules — the by-NAME way to say which lap each
 * repeated circuit point is contested on, so an editor never has to count raw KML marker indices.
 * One rule per line (or ';' separated): "name = laps", e.g.
 *   Tanunda Sprint = 1
 *   Lobethal = 1,2
 * The name is matched case-insensitively as a substring of the point's label (see matchActiveLaps),
 * which is robust to plotaroute's ~10-char name truncation — type a short distinctive token. Lines
 * without "name = laps" or with no valid lap number are skipped.
 * @returns {Array<{match:string, laps:string}>}
 */
export function parsePointLapRules(text) {
    if (!text) return [];
    return String(text)
        .split(/[\n;]+/)
        .map((line) => {
            const eq = line.indexOf('=');
            if (eq < 0) return null;
            const match = line.slice(0, eq).trim();
            const laps = line.slice(eq + 1).trim();
            if (!match || !parseActiveLaps(laps)) return null;
            return { match, laps };
        })
        .filter(Boolean);
}

// Friendly display name per race-feature type — used when the source label is missing or a bare
// truncation. Mirrors the UI's TYPE_LABEL but lives here so the pure label logic stays testable.
export const TYPE_DISPLAY_NAME = {
    START_NEUTRAL: 'Start',
    START: 'Start',
    END_NEUTRAL: 'End of race neutral',
    KOM: 'KOM / QOM',
    SPRINT: 'Sprint',
    FEED: 'Feed zone',
    FINISH: 'Finish',
    VANTAGE: 'Vantage point',
};

// Canonical name fragments per type. A source label that is just a (possibly truncated) prefix of
// one of these carries no location detail — it's the plotaroute ~10-char truncation ("King of th",
// "Feed &", "Ziptrak Sp", "Race Finis") that reads as broken — so it gets swapped for the friendly
// type name. A real location label ("Feed Zone – Angaston", "Stirling") is NOT a prefix of these,
// so it is kept verbatim.
const TYPE_CANON = {
    START_NEUTRAL: ['start race', 'start neutral', 'neutral start', 'start'],
    START: ['start'],
    END_NEUTRAL: ['end of race neutral', 'end race neutral', 'end neutral', 'end of neutral'],
    KOM: ['king of the mountain', 'queen of the mountain', 'king / queen', 'queen / king', 'kom', 'qom'],
    SPRINT: ['ziptrak sprint', 'ziptrak® sprint', 'sprint'],
    FEED: ['feed zone', 'feed'],
    FINISH: ['race finish', 'stage finish', 'finish'],
    VANTAGE: ['vantage point', 'vantage'],
};

/**
 * Is this source label a bare/truncated generic for its type, carrying no location detail? True for
 * empty labels and for plotaroute truncations like "Feed &", "King of th", "Ziptrak Sp", "Race
 * Finis" — each just a prefix of the type's own name. False for real location names ("Feed Zone –
 * Angaston", "Stirling"), which aren't a prefix of any canonical type phrase. Drives the friendly
 * display-label fallback in buildStage.
 */
export function isWeakLabel(label, type) {
    const cleaned = String(label || '').replace(/&\s*$/, '').trim().toLowerCase();
    if (!cleaned) return true;
    const canonicalPhrases = TYPE_CANON[type];
    if (!canonicalPhrases) return false;
    return canonicalPhrases.some((phrase) => phrase.startsWith(cleaned));
}

/**
 * Resolve the editor's per-point lap rules against a marker label. Returns the first matching rule's
 * laps (as a 1-based array), or null. The match is a case-insensitive substring test either way round
 * so a short editor token ("Tanunda") matches a long label ("Ziptrak® Sprint – Tanunda") and a long
 * editor name still matches a plotaroute-truncated label ("Ziptrak® S"). Lets an editor pin which lap
 * each sprint / end-of-neutral is contested on by NAME instead of by KML index.
 */
export function matchActiveLaps(label, rules) {
    if (!label || !Array.isArray(rules)) return null;
    const hay = String(label).toLowerCase();
    for (const rule of rules) {
        const needle = String((rule && rule.match) || '').trim().toLowerCase();
        if (!needle) continue;
        if (hay.includes(needle) || needle.includes(hay)) {
            const laps = parseActiveLaps(rule.laps);
            if (laps) return laps;
        }
    }
    return null;
}
